import { t } from "@/i18n-js/instance";
import { RsvpActionButton } from "@circle-react/components/Events/NewEventsSpace/EventDetailPage/RsvpActionButton";
import { ListViewTicketInfo } from "@circle-react/components/Events/NewEventsSpace/ListViewItem/ListViewTicketInfo";
import { canRespondToInvitation } from "@circle-react/components/Events/NewEventsSpace/shared/helpers";
import {
  RSVP_STATUS,
  STATUS,
} from "@circle-react/components/Events/helpers/constants";
import { isPaidEvent } from "@circle-react/components/EventsV3/Modal/utils";
import { dateIsPast } from "@circle-react/helpers/dateTimeHelpers";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { eventsShowPath } from "@circle-react/helpers/urlHelpers";
import { Typography } from "@circle-react-shared/uikit/Typography";
import { Button } from "@circle-react-uikit/Button";
import { SkeletonLoader } from "@circle-react-uikit/SkeletonLoader";
import { useEvent } from "../../hooks/useEvent";
import { EventTimestamp } from "./EventTimeStamp";
import { Location } from "./Location";

export const EventPopoverContent = ({
  title,
  onChange,
  event: eventInfo = {},
}: any) => {
  const { eventData = {}, isLoading } = useEvent({ event: eventInfo });

  if (isLoading)
    return (
      <div
        className="bg-primary border-primary h-auto min-h-[158px] min-w-[400px] max-w-[500px] rounded-lg border shadow"
        data-testid="event-detail-popover"
        id="event-detail-popover"
      >
        <SkeletonLoader variant="event-popover" />
      </div>
    );

  const event = { ...eventInfo, ...eventData };
  const {
    event_setting_attributes: {
      ends_at: endsAt,
      hide_location_from_non_attendees: shouldHideLocationFromNonAttendees,
    },
    rsvped_event: rsvpedEvent,
    status,
    rsvp_status: rsvpStatus,
    cover_image_url: coverImageUrl,
    paywall_attributes: ticketData,
  } = event || {};
  const isPublished = status === STATUS.PUBLISHED;

  const hasEventFinished = !!endsAt && dateIsPast(endsAt);
  const shouldHideLocationFromCurrentMember =
    shouldHideLocationFromNonAttendees && !rsvpedEvent;

  const shouldDisplayRsvpAction =
    isPublished && !hasEventFinished && canRespondToInvitation(event);
  const isAttendeeInvited = rsvpStatus == RSVP_STATUS.INVITED;

  return (
    <div
      className="bg-primary border-primary h-auto min-w-[400px] max-w-[500px] rounded-lg border shadow"
      data-testid="event-detail-popover"
      id="event-detail-popover"
    >
      {coverImageUrl && (
        <img
          loading="lazy"
          alt=""
          src={coverImageUrl}
          className="relative h-36 w-full rounded-t-lg object-cover"
        />
      )}
      <div className="p-6">
        <Typography.TitleSm weight="semibold">{title}</Typography.TitleSm>
        <div className="mt-4 flex items-center gap-4">
          <EventTimestamp eventData={event?.event_setting_attributes} />
          {!shouldHideLocationFromCurrentMember && <Location event={event} />}
          {isPaidEvent(event) && <ListViewTicketInfo ticketData={ticketData} />}
        </div>
        <div
          className={classNames("mt-4 flex w-full gap-4", {
            "flex-col": isAttendeeInvited,
            "items-center": !isAttendeeInvited,
          })}
        >
          {shouldDisplayRsvpAction && (
            <RsvpActionButton event={event} onChange={onChange} isFullWidth />
          )}
          <Button
            type="button"
            variant="secondary"
            className="!border-primary"
            full
            onClick={() => {
              window.open(
                eventsShowPath({
                  spaceSlug: event.space_slug,
                  eventSlug: event.slug,
                }),
                "_self",
              );
            }}
          >
            {t("learn_more")}
          </Button>
        </div>
      </div>
    </div>
  );
};
